<!-- eslint-disable vue/no-static-inline-styles -->
<script lang="ts" setup>
import {
  ref, computed, onMounted,
} from 'vue';
import { process } from '@progress/kendo-data-query';
import * as zod from 'zod';
import {
  Grid, GridDataStateChangeEvent, GridColumnProps, GridSortChangeEvent,
} from '@progress/kendo-vue-grid';
import { TypedSchema } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { uniqueRulesRaw, uniqueRules } from '@/lib/generate-rules';
import useFlashMessageStore from '@/stores/flash-messages';
import { InputType } from '@/interfaces/InputFields';
import ConnxForm from '@/components/Molecules/Field/ConnxForm.vue';
import ConnxField from '@/components/Molecules/Field/ConnxField.vue';
import ConnxConfirmationModal from '@/components/Molecules/ConnxConfirmationModal.vue';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import AdminEditControls from '@/views/Partials/AdminSidebarEditControls.vue';
import useHRAdminPronounStore from '@/stores/HRAdmin/pronouns';

const test = ref();

interface GridData {
  id: number;
  description: string;
  editing: boolean;
}

const pronounStore = useHRAdminPronounStore();
const messageStore = useFlashMessageStore();

await pronounStore.init();

const sort = ref();
const key = ref(0);
const selectedField = ref('editing');
const recordBeingEdited = ref();
const recordBeingCreated = ref('');
const deleteConfirmationOpen = ref(false);
const records = ref<GridData[]>(pronounStore.pronouns.map((record) => ({
  ...record,
  editing: false,
})));
const gridData = ref();

const clearErrorIfEmpty = () => {
  if (recordBeingCreated.value === '') {
    key.value += 1;
  }
}

const columns = computed(() => [
  {
    field: 'description',
    title: 'Pronoun',
    cell: 'PronounCellTemplate',
  },
] as GridColumnProps[]);

const getData = (data: GridData[]) => process(data, {
  sort: sort.value,
});

const dataStateChange = (event: GridDataStateChangeEvent | GridSortChangeEvent) => {
  if (Object.keys(event).includes('sort')) {
    sort.value = (event as GridSortChangeEvent).sort;
  }

  gridData.value = getData(records.value);
};

onMounted(() => {
  sort.value = [{ field: 'description', dir: 'asc' }];
  gridData.value = getData(records.value);
});

const toggleEdit = (record: GridData, editing: boolean = true) => {
  recordBeingEdited.value = !editing ? null : record;
  records.value = records.value.map((item) => {
    if (item.id === record.id) {
      return {
        ...item,
        editing,
      };
    }
    return {
      ...item,
      editing: false,
    };
  });
  gridData.value = getData(records.value);
};

const updateRecord = async () => {
  const updatedPronoun = await pronounStore.updatePronoun(recordBeingEdited.value);
  messageStore.pushNotification({
    id: `Pronoun_${recordBeingEdited.value.id}`,
    type: 'success',
    message: `Pronoun '${recordBeingEdited.value?.description}' updated successfully.`,
  });
  if (updatedPronoun) {
    records.value = records.value.map((record) => {
      if (record.id === updatedPronoun.id) {
        return { ...updatedPronoun, editing: false };
      }
      return record;
    });

    gridData.value = getData(records.value);
  }

};

const toggleDeleteConfirmationOpen = (record: GridData | null = null) => {
  recordBeingEdited.value = record;
  deleteConfirmationOpen.value = !deleteConfirmationOpen.value;
};

const deleteRecord = async () => {
  await pronounStore.deletePronoun(recordBeingEdited.value.id);
  messageStore.pushNotification({
    id: `Pronoun_${recordBeingEdited.value.id}`,
    type: 'success',
    message: `Pronoun '${recordBeingEdited.value?.description}' deleted successfully.`,
  });
  records.value = records.value.filter((item) => item.id !== recordBeingEdited.value.id);
  gridData.value = getData(records.value);
  toggleDeleteConfirmationOpen();
};

const createNewRecord = async () => {
  const responseInsertedRecord = await pronounStore.insertPronoun(recordBeingCreated.value);
  messageStore.pushNotification({
    id: `Pronoun_${responseInsertedRecord?.id}`,
    type: 'success',
    message: `Pronoun '${responseInsertedRecord?.description}' created successfully.`,
  });
  if (responseInsertedRecord) {
    records.value = [...records.value, {
      ...responseInsertedRecord,
      editing: false,
    }];
    gridData.value = getData(records.value);

    recordBeingCreated.value = '';
    key.value += 1;
  }
};

const pronounDescriptions = records.value.map((i) => i.description);

const validationSchema = computed((): TypedSchema => toTypedSchema(zod.object({
  pronoun: uniqueRulesRaw('Pronoun', pronounDescriptions, '', true, 'Please enter a Pronoun value.'),
})));
</script>

<template>
  <div>
    <div class="tw-flex tw-flex-wrap tw-space-x-4">
      <div class="tw-order-2 md:tw-order-1">
        <grid
          ref="grid"
          class="tw-w-152"
          scrollable="none"
          :data-items="gridData"
          :row-class="() => 'tw-group'"
          :sortable="false"
          :sort="sort"
          :selectable="true"
          :selected-field="selectedField"
          :columns="columns"
          @datastatechange="dataStateChange"
          @sortchange="dataStateChange"
        >
          <template #PronounCellTemplate="{ props }">
            <td class="k-table-td">
              <div class="tw-flex tw-items-center tw-justify-between">
                <connx-field
                  v-if="props.dataItem.editing"
                  v-model="recordBeingEdited.description"
                  :type="InputType.Text"
                  :maxlength="50"
                  name="description"
                  :required="true"
                  :absolute-error="true"
                  :rules="uniqueRules('Pronoun', pronounDescriptions, props.dataItem.description, true, 'Please enter a Pronoun value.')"
                  class="tw-mb-0 tw-w-96 tw-flex-shrink-0"
                >
                </connx-field>
                <span
                  v-else
                  class="tw-flex-shrink-0"
                >
                  {{ props.dataItem.description }}
                </span>
                <admin-edit-controls
                  class="tw-h-[19px] tw-w-full"
                  :editing="props.dataItem.editing"
                  :save-disabled="recordBeingEdited?.description.length <= 0"
                  @saved="updateRecord()"
                  @editing="(editing) => toggleEdit(props.dataItem, editing)"
                  @deleted="toggleDeleteConfirmationOpen(props.dataItem)"
                ></admin-edit-controls>
              </div>
            </td>
          </template>
        </grid>
      </div>
      <connx-form
        ref="form"
        v-slot="{ errors }"
        class="tw-order-2 md:tw-order-2"
        :validation-schema="validationSchema"
        @valid-submission="createNewRecord()"
      >
        <h5 class="tw-mb-4">
          New Pronoun
        </h5>
        <connx-field
          :key="`new-pronoun-${key}`"
          v-model="recordBeingCreated"
          :type="InputType.Text"
          @blur="clearErrorIfEmpty"
          :maxlength="50"
          class="tw-min-w-96 tw-mb-2"
          name="pronoun"
          label="Pronoun"
          :required="true"
        >
          Pronoun
        </connx-field>
        <connx-button
          type="submit"
          button-style="brand-primary"
          icon="fas fa-plus"
          button-size="sm"
          icon-position="before"
          :disabled="Object.keys(errors).length !== 0"
        >
          Add
        </connx-button>
      </connx-form>
    </div>

    <connx-confirmation-modal
      :open="deleteConfirmationOpen"
      confirmation-button-text="Yes, delete it."
      @confirmed="deleteRecord()"
      @close="toggleDeleteConfirmationOpen()"
    >
    </connx-confirmation-modal>
  </div>
</template>
